import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageCircle, Compass, Layers, Sparkles } from 'lucide-react';
import { SplitWords } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const STEPS = [
  { number: '01', icon: MessageCircle, title: 'Book a Conversation', description: '30 minutes. No obligation. Just clarity about where you are and what you need.' },
  { number: '02', icon: Compass, title: 'We Design Your Engagement', description: 'A bespoke approach based on your unique situation — no cookie-cutter programs.' },
  { number: '03', icon: Layers, title: 'Deep Work Together', description: 'Weekly or bi-weekly sessions. Team retreats when needed. Real transformation.' },
  { number: '04', icon: Sparkles, title: 'Sustainable Transformation', description: 'I make myself irrelevant as quickly as possible. You keep the tools forever.' },
];

function TimelineStep({ step, index, progress }) {
  const stepProgress = useTransform(progress, [index * 0.2, (index + 1) * 0.2], [0, 1]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 border-b border-[#F7F5F2]/8 last:border-b-0 group"
    >
      {/* Number + line */}
      <div className="md:col-span-2 flex items-start">
        <motion.span
          className="font-serif text-3xl md:text-4xl text-[#C9A96E]/30 leading-none group-hover:text-[#C9A96E] transition-colors duration-500"
          whileHover={{ scale: 1.2 }}
          style={{ opacity: stepProgress }}
        >
          {step.number}
        </motion.span>
      </div>

      {/* Icon + Title */}
      <div className="md:col-span-4 flex items-start gap-4">
        <motion.div
          className="w-10 h-10 border border-[#C9A96E]/20 flex items-center justify-center shrink-0 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E]/10 transition-all duration-500"
          whileHover={{ rotate: 90 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <step.icon className="w-5 h-5 text-[#C9A96E]" />
        </motion.div>
        <h3 className="font-serif text-xl md:text-2xl text-[#F7F5F2] leading-tight group-hover:text-[#C9A96E] transition-colors duration-300">
          {step.title}
        </h3>
      </div>

      {/* Description */}
      <div className="md:col-span-6">
        <p className="text-base text-[#F7F5F2]/60 font-sans leading-relaxed md:pl-4 group-hover:text-[#F7F5F2]/90 transition-colors duration-300">
          {step.description}
        </p>
      </div>
    </motion.div>
  );
}

export default function ProcessSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  return (
    <section
      ref={sectionRef}
      id="process"
      data-testid={TEST_IDS.processSection}
      className="bg-[#1B2A4A] py-24 md:py-32 px-6 md:px-12 relative overflow-hidden"
    >
      <div className="dot-pattern absolute inset-0" />

      {/* Animated timeline line */}
      <motion.div
        className="absolute top-0 left-1/2 w-px bg-[#C9A96E]/10 hidden lg:block"
        style={{ scaleY: scrollYProgress, transformOrigin: 'top', height: '100%' }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
            />
            How It Works
          </p>
          <h2
            data-testid={TEST_IDS.processHeadline}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#F7F5F2] max-w-xl"
          >
            <SplitWords>Your Path to Clarity and Breakthrough</SplitWords>
          </h2>
        </motion.div>

        <div className="space-y-0">
          {STEPS.map((step, i) => (
            <TimelineStep key={step.number} step={step} index={i} progress={scrollYProgress} />
          ))}
        </div>
      </div>
    </section>
  );
}
